import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import PhoneSetup from '../measuring/PhoneSetup';
import { ViewAsPicker } from '../components/ViewAs';

export default function Account() {
  const navigate    = useNavigate();
  const { user, login, realLevel } = useAuth();

  const [name, setName]             = useState(user?.name      || '');
  const [jobTitle, setJobTitle]     = useState(user?.job_title  || '');
  const [phone, setPhone]           = useState(user?.phone      || '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg]       = useState(null); // { type: 'ok'|'err', text }

  const [curPwd, setCurPwd]         = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [cfmPwd, setCfmPwd]         = useState('');
  const [savingPwd, setSavingPwd]   = useState(false);
  const [pwdMsg, setPwdMsg]         = useState(null);

  const [pinState, setPinState]     = useState({ has_pin: false, locked: false });
  const [pinPwd, setPinPwd]         = useState('');
  const [pin, setPin]               = useState('');
  const [savingPin, setSavingPin]   = useState(false);
  const [pinMsg, setPinMsg]         = useState(null);

  useEffect(() => {
    api.get('/api/v1/auth/pin')
      .then(r => setPinState(r.data || { has_pin: false, locked: false }))
      .catch(() => {});   // a missing PIN is not an error worth showing
  }, []);

  async function savePin(e) {
    e.preventDefault();
    if (pin.length < 4) { setPinMsg({ type: 'err', text: 'A PIN must be at least 4 digits' }); return; }
    setSavingPin(true); setPinMsg(null);
    try {
      await api.post('/api/v1/auth/pin', { currentPassword: pinPwd, pin });
      setPinState({ has_pin: true, locked: false });
      setPin(''); setPinPwd('');
      setPinMsg({ type: 'ok', text: 'PIN saved' });
    } catch (err) {
      setPinMsg({ type: 'err', text: err.response?.data?.error || 'Could not save the PIN' });
    } finally {
      setSavingPin(false);
    }
  }

  async function removePin() {
    setSavingPin(true); setPinMsg(null);
    try {
      await api.delete('/api/v1/auth/pin', { data: { currentPassword: pinPwd } });
      setPinState({ has_pin: false, locked: false });
      setPin(''); setPinPwd('');
      setPinMsg({ type: 'ok', text: 'PIN removed — nobody can switch to you now' });
    } catch (err) {
      setPinMsg({ type: 'err', text: err.response?.data?.error || 'Could not remove the PIN' });
    } finally {
      setSavingPin(false);
    }
  }

  async function saveName(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSavingName(true);
    setNameMsg(null);
    try {
      const { data } = await api.put('/api/v1/auth/profile', { name: name.trim(), job_title: jobTitle || null, phone: phone || null });
      // Persist updated name in localStorage + context by calling login with existing token
      const token = localStorage.getItem('mouldos_token');
      const savedUser = JSON.parse(localStorage.getItem('mouldos_user') || '{}');
      login(token, { ...savedUser, name: data.user.name });
      setNameMsg({ type: 'ok', text: 'Name updated' });
    } catch (err) {
      setNameMsg({ type: 'err', text: err.response?.data?.error || 'Save failed' });
    }
    setSavingName(false);
  }

  async function savePassword(e) {
    e.preventDefault();
    if (newPwd.length < 6) { setPwdMsg({ type: 'err', text: 'New password must be at least 6 characters' }); return; }
    if (newPwd !== cfmPwd)  { setPwdMsg({ type: 'err', text: 'Passwords do not match' }); return; }
    setSavingPwd(true);
    setPwdMsg(null);
    try {
      await api.put('/api/v1/auth/profile', { currentPassword: curPwd, newPassword: newPwd });
      setCurPwd(''); setNewPwd(''); setCfmPwd('');
      setPwdMsg({ type: 'ok', text: 'Password changed successfully' });
    } catch (err) {
      setPwdMsg({ type: 'err', text: err.response?.data?.error || 'Save failed' });
    }
    setSavingPwd(false);
  }

  const initials = (user?.name || '?').charAt(0).toUpperCase();

  return (
    <div style={s.page}>
      <div style={s.breadcrumb}>
        <button onClick={() => navigate(-1)} style={s.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
      </div>

      <div style={s.topRow}>
        <div style={s.avatar}>{initials}</div>
        <div>
          <h1 style={s.title}>{user?.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={s.roleBadge}>{user?.role}</span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{user?.email}</span>
          </div>
        </div>
      </div>

      {realLevel === 'director' && (
        <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>View as</div>
          <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '10px' }}>See the app as a Setter or an Operator see it. Nothing can be saved while you do, and it ends when you close the app.</div>
          <ViewAsPicker />
        </div>
      )}

      <div style={s.grid}>

        {/* Profile */}
        <div style={s.card}>
          <div style={s.cardTitle}>Profile</div>
          <form onSubmit={saveName}>
            <Field label="Display name">
              <input
                style={s.input}
                value={name}
                onChange={e => { setName(e.target.value); setNameMsg(null); }}
                placeholder="Your name"
              />
            </Field>
            <Field label="Job title">
              <input
                style={s.input}
                value={jobTitle}
                onChange={e => { setJobTitle(e.target.value); setNameMsg(null); }}
                placeholder="e.g. Process Technician"
              />
            </Field>
            <Field label="Phone number">
              <input
                style={s.input}
                type="tel"
                value={phone}
                onChange={e => { setPhone(e.target.value); setNameMsg(null); }}
                placeholder="e.g. +386 40 123 456"
              />
            </Field>
            <Field label="Email address">
              <input style={{ ...s.input, opacity: 0.55, color: '#6b7280' }} value={user?.email || ''} disabled />
            </Field>
            <Field label="Role">
              <input style={{ ...s.input, opacity: 0.55, color: '#6b7280', textTransform: 'capitalize' }} value={user?.role || ''} disabled />
            </Field>
            {nameMsg && (
              <div style={{ ...s.msg, background: nameMsg.type === 'ok' ? 'rgba(134,239,172,0.08)' : '#7f1d1d', color: nameMsg.type === 'ok' ? '#86efac' : '#fca5a5', border: `1px solid ${nameMsg.type === 'ok' ? 'rgba(134,239,172,0.2)' : 'rgba(239,68,68,0.3)'}` }}>
                {nameMsg.text}
              </div>
            )}
            <button type="submit" style={{ ...s.saveBtn, opacity: savingName ? 0.6 : 1 }} disabled={savingName}>
              {savingName ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Security */}
        <div style={s.card}>
          <div style={s.cardTitle}>Change Password</div>
          <form onSubmit={savePassword}>
            <Field label="Current password">
              <input
                style={s.input}
                type="password"
                value={curPwd}
                onChange={e => { setCurPwd(e.target.value); setPwdMsg(null); }}
                placeholder="Your current password"
                autoComplete="current-password"
              />
            </Field>
            <Field label="New password">
              <input
                style={s.input}
                type="password"
                value={newPwd}
                onChange={e => { setNewPwd(e.target.value); setPwdMsg(null); }}
                placeholder="Min. 6 characters"
                autoComplete="new-password"
              />
            </Field>
            <Field label="Confirm new password">
              <input
                style={s.input}
                type="password"
                value={cfmPwd}
                onChange={e => { setCfmPwd(e.target.value); setPwdMsg(null); }}
                placeholder="Repeat new password"
                autoComplete="new-password"
              />
            </Field>
            {pwdMsg && (
              <div style={{ ...s.msg, background: pwdMsg.type === 'ok' ? 'rgba(134,239,172,0.08)' : '#7f1d1d', color: pwdMsg.type === 'ok' ? '#86efac' : '#fca5a5', border: `1px solid ${pwdMsg.type === 'ok' ? 'rgba(134,239,172,0.2)' : 'rgba(239,68,68,0.3)'}` }}>
                {pwdMsg.text}
              </div>
            )}
            <button type="submit" style={{ ...s.saveBtn, opacity: savingPwd ? 0.6 : 1 }} disabled={savingPwd || !curPwd || !newPwd || !cfmPwd}>
              {savingPwd ? 'Saving...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Switching PIN */}
        <div style={s.card}>
          <div style={s.cardTitle}>Switching PIN</div>

          <p style={s.explain}>
            Sets a short code so you can take over a phone or tablet someone else is signed in on,
            without them signing out. <b style={{ color: '#e5e7eb' }}>It only switches users on a device
            that is already signed in</b> — it can never sign you in on a new one.
          </p>

          {pinState.has_pin && (
            <div style={{ ...s.msg, background: 'rgba(20,184,166,0.08)', color: '#5eead4', border: '1px solid rgba(20,184,166,0.25)' }}>
              A PIN is set{pinState.locked ? ' — currently locked after too many wrong tries. It unlocks next time you sign in with your password.' : '. Others can switch to you on a shared device.'}
            </div>
          )}

          <form onSubmit={savePin}>
            <Field label="Your password">
              <input
                style={s.input}
                type="password"
                value={pinPwd}
                onChange={e => { setPinPwd(e.target.value); setPinMsg(null); }}
                placeholder="Confirm it's you"
                autoComplete="current-password"
              />
            </Field>
            <Field label={pinState.has_pin ? 'New PIN (4–8 digits)' : 'PIN (4–8 digits)'}>
              <input
                style={{ ...s.input, letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums' }}
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinMsg(null); }}
                placeholder="••••"
                autoComplete="off"
              />
            </Field>

            {pinMsg && (
              <div style={{ ...s.msg, background: pinMsg.type === 'ok' ? 'rgba(134,239,172,0.08)' : '#7f1d1d', color: pinMsg.type === 'ok' ? '#86efac' : '#fca5a5', border: `1px solid ${pinMsg.type === 'ok' ? 'rgba(134,239,172,0.2)' : 'rgba(239,68,68,0.3)'}` }}>
                {pinMsg.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="submit" style={{ ...s.saveBtn, opacity: savingPin ? 0.6 : 1 }} disabled={savingPin || !pinPwd || pin.length < 4}>
                {savingPin ? 'Saving...' : pinState.has_pin ? 'Change PIN' : 'Set PIN'}
              </button>
              {pinState.has_pin && (
                <button type="button" onClick={removePin} style={s.removeBtn} disabled={savingPin || !pinPwd}>
                  Remove PIN
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Getting MouldOS onto this phone (3 Nov 2026). */}
        <PhoneSetup />

      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  page:      { padding: '20px', fontFamily: 'system-ui, sans-serif', background: '#0f1117', minHeight: '100vh', color: '#f0f2f5' },
  breadcrumb:{ marginBottom: '16px' },
  backBtn:   { background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' },
  topRow:    { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px', flexWrap: 'wrap' },
  avatar:    { width: '52px', height: '52px', borderRadius: '50%', background: '#2a3040', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', color: '#14b8a6', flexShrink: 0 },
  title:     { fontSize: '22px', fontWeight: '700', margin: '0 0 4px 0' },
  roleBadge: { fontSize: '10px', fontWeight: '700', color: '#14b8a6', background: 'rgba(20,184,166,0.12)', border: '1px solid rgba(20,184,166,0.3)', borderRadius: '6px', padding: '2px 8px', textTransform: 'capitalize' },
  grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px', maxWidth: '1080px', alignItems: 'start' },
  card:      { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '20px' },
  cardTitle: { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#14b8a6', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #2a3040' },
  input:     { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '9px 12px', fontSize: '13px', fontFamily: 'system-ui, sans-serif', width: '100%', boxSizing: 'border-box' },
  msg:       { borderRadius: '8px', padding: '9px 12px', fontSize: '12px', marginBottom: '12px' },
  saveBtn:   { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', marginTop: '4px' },
  removeBtn: { background: 'none', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginTop: '4px' },
  explain:   { fontSize: '12.5px', color: '#9ca3af', lineHeight: 1.55, margin: '0 0 14px' },
};
